import { TicketDto } from '@trucktrax/trucktrax-ts-common';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { INCOMING_TICKETS_SELECTED } from '../../../constants/appConstants';
import { trackingSocketInit } from '../../../services/mapviewsService';
import { selectTruckMarker } from '../../../services/ticketsService';
import { ReduxState } from '../../../store';
import {
  clearSelectedMarker,
  closePopup,
  MarkerInfo,
  MarkerSelectedFrom,
  selectMarker,
  setActiveMarkers,
} from '../../../store/actions/mapviewActions';
import setSidebarSelected from '../../../store/actions/sidebarActions';
import { clearSelectedTicket, selectTicket } from '../../../store/actions/ticketActions';
import incomingActiveMarkersSelector, {
  filteredIncomingActivePlantListSelector,
} from '../../../store/selectors/incomingMaterialsSelectors';
import { determineRegionId } from '../../../util/appUtil';
import { isMapMarker, isMarkerType } from '../../../util/markerUtil';
import { incomingMarkerStatusMap } from '../../../util/statusesUtil';
import {
  ConnectedDispatchFunction,
  MapMarker,
  MapMarkerType,
  Marker,
  SidebarTitleType,
} from '../../../types';
import TTMap, { MarkerDataType, MarkerType } from '../map/TTMap';
import styles from '../map/MapWrapper.module.css';

interface IncomingMaterialsMapWrapperOwnProps {
  location: { lat: number; lng: number };
  handleZoomChange: (zoomLevel: number) => void;
  currentZoom: number;
  handleCenterChange: (center: { lat: number; lng: number }) => void;
  onMapReady?: () => void;
}

export class IncomingMaterialsMapWrapper extends Component<IncomingMaterialsMapWrapperAllProps> {
  componentDidMount() {
    if (this.props.currentRegion?.url) {
      trackingSocketInit(this.props.currentRegion.url);
    }
    this.props.setActiveMarkers(this.props.incomingMarkers);
  }

  componentWillUnmount() {
    // Clear so App.tsx detects a change and restores the full marker set
    this.props.setActiveMarkers([]);
  }

  componentDidUpdate(prevProps: IncomingMaterialsMapWrapperAllProps) {
    if (
      prevProps.currentRegion?.url !== this.props.currentRegion?.url &&
      this.props.currentRegion?.url
    ) {
      trackingSocketInit(this.props.currentRegion.url);
    }
    if (prevProps.incomingMarkers !== this.props.incomingMarkers) {
      this.props.setActiveMarkers(this.props.incomingMarkers);
    }
  }

  handleMarkerClick = (targetMarker: MarkerType | MarkerInfo | MapMarker) => {
    if (targetMarker.type === MapMarkerType.Plant) {
      this.props.selectMarker(targetMarker as unknown as MarkerInfo);
      return;
    }

    let ticketUrl = '';
    if (isMarkerType(targetMarker)) {
      ticketUrl = (targetMarker.markerData as MarkerDataType)?.ticket?.url ?? '';
    } else if (isMapMarker(targetMarker)) {
      ticketUrl = targetMarker.data?.ticket?.url ?? '';
    }

    this.props.selectTruckMarker(
      { ...targetMarker, selectedFrom: MarkerSelectedFrom.MapMarker } as MarkerType,
      ticketUrl,
    );
  };

  handleMarkerClose = (targetMarker: MarkerType) => {
    this.props.closePopup(
      targetMarker.markerId ?? '',
      this.props.currentRegionId,
      this.props.selectedProductLine,
    );
    this.props.clearSelectedMarker();
  };

  ticketInfoTicketsCallBack = (marker: Marker) => {
    this.props.clearSelectedTicket();
    this.props.selectTicket(marker.ticket as TicketDto);
    this.props.setSidebarSelected(INCOMING_TICKETS_SELECTED, true);
  };

  handleCenterChanged = (center?: { lat: number; lng: number }) => {
    if (center) {
      this.props.handleCenterChange(center);
    }
  };

  // eslint-disable-next-line class-methods-use-this
  noopStringCallback = () => '';

  render() {
    // Pass empty lists for features not used on the incoming materials map
    return (
      <div className={styles.mapPane} data-test="incoming-materials-map">
        <TTMap
          center={this.props.location}
          currentRegion={this.props.currentRegion!}
          plantList={this.props.plantList}
          popupToggleMap={this.props.popupToggleMap}
          ordersList={[]}
          showPlantsGeofences={this.props.showPlantsGeofences}
          showJobsitesGeofences={false}
          selectedProductLine={this.props.selectedProductLine}
          onMarkerClick={this.handleMarkerClick}
          onMarkerClose={this.handleMarkerClose}
          updateToFieldValueWithPlantDrivers={this.noopStringCallback}
          ticketInfoTicketsCallBack={this.ticketInfoTicketsCallBack}
          handleCenterChanged={this.handleCenterChanged}
          // DEV-3924: status labels come from each marker's paired ticket (origination
          // plant product line), never the leftover main-map selectedProductLine.
          markerStatusMapResolver={incomingMarkerStatusMap}
          onZoomChange={this.props.handleZoomChange}
          currentZoom={this.props.currentZoom}
          onMapReady={this.props.onMapReady}
          isMessagingEnabled={false}
        />
      </div>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    selectedMarker: state.selectedMarker,
    popupToggleMap: state.popupToggleMap,
    currentRegion: state.currentRegion,
    plantList: filteredIncomingActivePlantListSelector(state),
    selectedProductLine: state.selectedProductLine,
    incomingMarkers: incomingActiveMarkersSelector(state),
    currentRegionId: determineRegionId(state.currentRegion),
    showPlantsGeofences: state.mapFilters?.showPlantsGeofences ?? false,
  };
}

type IncomingMaterialsMapWrapperReduxStateProps = ReturnType<typeof mapStateToProps>;

interface IncomingMaterialsMapWrapperDispatchProps {
  closePopup: ConnectedDispatchFunction<typeof closePopup>;
  clearSelectedMarker: () => void;
  clearSelectedTicket: () => void;
  selectTicket: (ticket: TicketDto) => void;
  setSidebarSelected: (sidebarTitle: SidebarTitleType, isSelected: boolean) => void;
  selectMarker: ConnectedDispatchFunction<typeof selectMarker>;
  selectTruckMarker: ConnectedDispatchFunction<typeof selectTruckMarker>;
  setActiveMarkers: (markers: MapMarker[]) => void;
}

type IncomingMaterialsMapWrapperAllProps = IncomingMaterialsMapWrapperOwnProps &
  IncomingMaterialsMapWrapperReduxStateProps &
  IncomingMaterialsMapWrapperDispatchProps;

export default connect(mapStateToProps, {
  closePopup,
  clearSelectedMarker,
  selectTicket,
  setSidebarSelected,
  clearSelectedTicket,
  selectTruckMarker,
  selectMarker,
  setActiveMarkers,
})(IncomingMaterialsMapWrapper);
